import { useMemo, useState } from "react";
import { fmtMoney, fmtPct } from "../parsers/xmlParser";
import { CategoryBadge, CompanyLogo } from "../components/UI";
import { CATEGORY_STYLE } from "../utils/fieldMaps";

/**
 * ClientPortalFunds - A simplified, elegant funds list exclusively for the Client Portal.
 * It removes all advisor-specific technical flags (190, dmeiTzv, hk, validDate, xml flags)
 * and focuses on what the client cares about: Company, Product, Number, Balance, and Yield.
 */
export default function ClientPortalFunds({ funds }) {
  if (!funds || funds.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
        לא נמצאו נתונים להצגה
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {funds.map((f, idx) => (
        <PortalFundCard key={f.id || idx} fund={f} />
      ))}
    </div>
  );
}

function PortalFundCard({ fund }) {
  const [expanded, setExpanded] = useState(false);
  const cs = CATEGORY_STYLE[fund.cat] || CATEGORY_STYLE["ביטוח מנהלים"];

  const redeem = fund.redeem || 0;
  const tsua = fund.returns?.neto || fund.returns?.bruto1 || fund.returns?.bruto2 || null;

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        background: "white",
        borderRadius: 16,
        padding: "16px 20px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #e2e8f0",
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Accent Top Bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: cs.accent }} />

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        {/* Right side: Company Logo + Name + Policy details */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", flex: 1 }}>
          <CompanyLogo code={fund.companyCode} size={42} />
          
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>
                {fund.companyName}
              </span>
              <CategoryBadge cat={fund.cat} />
              {fund._ownerName && (
                <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                  👤 {fund._ownerName.split(' ')[0]}
                </span>
              )}
            </div>
            
            <div style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
              {fund.tochnit || "מוצר כללי"}
            </div>
            
            <div style={{ fontSize: 12, color: "#94a3b8", display: "flex", gap: 6 }}>
              <span>מס׳ חשבון:</span>
              <span style={{ fontFamily: "monospace", fontSize: 13 }}>{fund.polNum}</span>
            </div>
          </div>
        </div>

        {/* Left side: Financials */}
        <div style={{ textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {/* Main Balance */}
          {fund.monthlyPremiumTotal != null && redeem === 0 ? (
             <div style={{ fontWeight: 800, fontSize: 18, color: cs.accent }}>
                {fmtMoney(fund.monthlyPremiumTotal)}
                <span style={{ fontSize: 12, fontWeight: 500, marginRight: 4, color: "#64748b" }}>/חודש</span>
             </div>
          ) : (
             <div style={{ fontWeight: 800, fontSize: 18, color: cs.accent }}>
               {fmtMoney(redeem)}
             </div>
          )}

          {/* Yield (if applicable) */}
          {tsua != null && (
            <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ color: "#94a3b8", fontWeight: 500 }}>תשואה</span>
              <span style={{ background: "#dcfce7", padding: "2px 6px", borderRadius: 6 }}>
                {fmtPct(tsua)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded State: Simplified breakdown */}
      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f1f5f9", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
           <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
           {fund.employment?.sugTochnit && (
                 <div style={{ fontSize: 13, color: "#475569" }}>
                   <span style={{ color: "#94a3b8", marginLeft: 8 }}>מעמד:</span>
                   <span style={{ fontWeight: 600 }}>{fund.employment.sugTochnit}</span>
                 </div>
              )}
              {fund.statusLabel && (
                 <div style={{ fontSize: 13, color: "#475569" }}>
                   <span style={{ color: "#94a3b8", marginLeft: 8 }}>סטטוס:</span>
                   <span style={{ fontWeight: 600 }}>{fund.statusLabel}</span>
                 </div>
              )}
              {fund.joinDate && (
                 <div style={{ fontSize: 13, color: "#475569" }}>
                   <span style={{ color: "#94a3b8", marginLeft: 8 }}>הצטרפות:</span>
                   <span>{fund.joinDate.split("-").reverse().join("/")}</span>
                 </div>
              )}
              {fund.dmeiNihul?.actual?.dmeiTzvPct != null && (
                 <div style={{ fontSize: 13, color: "#475569" }}>
                   <span style={{ color: "#94a3b8", marginLeft: 8 }}>ד. ניהול צבירה:</span>
                   <span style={{ fontWeight: 600 }}>{fmtPct(fund.dmeiNihul.actual.dmeiTzvPct)}</span>
                 </div>
              )}
              {fund.dmeiNihul?.actual?.dmeiHafPct != null && (
                 <div style={{ fontSize: 13, color: "#475569" }}>
                   <span style={{ color: "#94a3b8", marginLeft: 8 }}>ד. ניהול הפקדה:</span>
                   <span style={{ fontWeight: 600 }}>{fmtPct(fund.dmeiNihul.actual.dmeiHafPct)}</span>
                 </div>
              )}
           </div>

           {fund.maslulim?.length > 0 && (
             <div style={{ fontSize: 12, color: "#64748b" }}>
                <div style={{ fontWeight: 600, color: "#475569", marginBottom: 4 }}>מסלולי השקעה:</div>
                {Object.entries(
                  fund.maslulim.reduce((acc, m) => {
                    const name = m.name || "כללי";
                    acc[name] = (acc[name] || 0) + (m.balance || 0);
                    return acc;
                  }, {})
                ).map(([name, bal], i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span style={{ paddingLeft: 8 }} title={name}>
                      {name}
                    </span>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>{fmtMoney(bal)}</span>
                  </div>
                ))}
             </div>
           )}
        </div>
      )}
    </div>
  );
}
